/**
 * Pure calculation functions for the business loan calculator.
 *
 * No React, no side effects — every function is deterministic and unit-tested
 * against the worked examples quoted in the article (see tests/unit/loan-math.test.ts).
 * Full precision is kept internally; rounding happens only at display time.
 */

export interface LoanSummary {
  monthlyPayment: number;
  totalRepaid: number;
  totalInterest: number;
}

/**
 * Monthly payment on an amortizing fixed-rate term loan.
 *
 * M = P × [r(1 + r)^n] / [(1 + r)^n − 1], where r = (APR / 100) / 12.
 * Handles the 0% edge case (straight-line repayment).
 */
export function monthlyPayment(
  principal: number,
  annualRatePct: number,
  nMonths: number
): number {
  if (nMonths <= 0 || principal <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal / nMonths;
  const pow = Math.pow(1 + r, nMonths);
  return (principal * (r * pow)) / (pow - 1);
}

export function loanSummary(
  principal: number,
  annualRatePct: number,
  nMonths: number
): LoanSummary {
  const payment = monthlyPayment(principal, annualRatePct, nMonths);
  const totalRepaid = payment * nMonths;
  return {
    monthlyPayment: payment,
    totalRepaid,
    totalInterest: totalRepaid - principal,
  };
}

/**
 * Solve for the monthly rate that equates the net proceeds to the payment stream.
 *
 * The present value of an annuity, pay × (1 − (1 + i)^−n) / i, decreases
 * monotonically in i, so we bisect on [1e-9, 1.0].
 */
export function solveMonthlyIRR(net: number, pay: number, n: number): number {
  if (pay <= 0 || n <= 0 || net <= 0) return 0;
  const pv = (i: number) => (pay * (1 - Math.pow(1 + i, -n))) / i;
  let lo = 1e-9;
  let hi = 1.0;
  for (let k = 0; k < 100; k++) {
    const mid = (lo + hi) / 2;
    const diff = pv(mid) - net;
    if (Math.abs(diff) < 1e-7) return mid;
    // pv is decreasing in i: if pv is still too high, push the rate up.
    if (diff > 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * True APR including an upfront origination fee, using the US nominal-APR
 * convention (monthly rate × 12). With no fee, the true APR equals the headline rate.
 */
export function trueAPR(
  principal: number,
  annualRatePct: number,
  nMonths: number,
  originationFeePct: number
): number {
  if (originationFeePct <= 0) return annualRatePct;
  const pay = monthlyPayment(principal, annualRatePct, nMonths);
  const net = principal * (1 - originationFeePct / 100);
  return solveMonthlyIRR(net, pay, nMonths) * 12 * 100;
}

export interface ScheduleRow {
  /** 1-based month number. */
  period: number;
  payment: number;
  principalPaid: number;
  interestPaid: number;
  /** Balance remaining after this payment. */
  balance: number;
}

export interface YearRow {
  /** 1-based year number. */
  year: number;
  payment: number;
  principalPaid: number;
  interestPaid: number;
  /** Balance remaining at the end of the year. */
  balance: number;
}

/**
 * Month-by-month amortization schedule. The final payment is trued up so the
 * balance lands exactly on zero (absorbing accumulated rounding).
 */
export function buildSchedule(
  principal: number,
  annualRatePct: number,
  nMonths: number
): ScheduleRow[] {
  if (nMonths <= 0 || principal <= 0) return [];
  const pay = monthlyPayment(principal, annualRatePct, nMonths);
  const r = annualRatePct / 100 / 12;
  const rows: ScheduleRow[] = [];
  let balance = principal;
  for (let month = 1; month <= nMonths; month++) {
    const interestPaid = balance * r;
    let principalPaid = pay - interestPaid;
    let payment = pay;
    // Last month (or any rounding overshoot) clears the remaining balance exactly.
    if (month === nMonths || principalPaid > balance) {
      principalPaid = balance;
      payment = balance + interestPaid;
    }
    balance -= principalPaid;
    if (balance < 0.005) balance = 0;
    rows.push({ period: month, payment, principalPaid, interestPaid, balance });
  }
  return rows;
}

/** Roll a monthly schedule up into calendar years (12 months each). */
export function aggregateByYear(rows: ScheduleRow[]): YearRow[] {
  const years: YearRow[] = [];
  for (const row of rows) {
    const index = Math.floor((row.period - 1) / 12);
    const year = (years[index] ??= {
      year: index + 1,
      payment: 0,
      principalPaid: 0,
      interestPaid: 0,
      balance: 0,
    });
    year.payment += row.payment;
    year.principalPaid += row.principalPaid;
    year.interestPaid += row.interestPaid;
    year.balance = row.balance;
  }
  return years;
}
